import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { MsalProvider } from '@azure/msal-react';
import { EventType, type AuthenticationResult } from '@azure/msal-browser';

import { msalInstance } from './auth/msalConfig';
import { queryClient } from './lib/queryClient';
import { LightboxProvider } from './features/attachments/Lightbox';
import './index.css';

// MSAL renews the API token silently by loading the redirect URI in a HIDDEN
// IFRAME. If we're inside that iframe, do NOT boot the SPA: its router + auto
// sign-in would navigate the frame away from the auth response before MSAL's
// parent window can read it, which fails as `monitor_window_timeout` ("couldn't
// load your tickets"). MSAL reads the response straight from this frame's URL,
// so the iframe only needs to load and sit still — we render nothing.
if (window.top === window.self) {
  // Wire MSAL: ensure handleRedirectPromise has resolved before render so any
  // in-flight sign-in (loginRedirect callback) is consumed before the router
  // decides where to send the user. Wrapped so a redirect-handling failure
  // (stale/aborted interaction state, misconfigured authority, etc.) NEVER
  // prevents the app from rendering — otherwise the user gets a blank page.
  try {
    await msalInstance.initialize();
    const result = await msalInstance.handleRedirectPromise();
    if (result?.account) {
      msalInstance.setActiveAccount(result.account);
    } else if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts()[0]) {
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    }
  } catch (err) {
    // Render anyway; the user lands on /login and can retry sign-in.
    console.error('[msal] redirect handling failed; rendering app regardless:', err);
  }

  msalInstance.addEventCallback((evt) => {
    if (evt.eventType === EventType.LOGIN_SUCCESS && evt.payload) {
      const payload = evt.payload as AuthenticationResult;
      msalInstance.setActiveAccount(payload.account);
    }
  });

  // Import the router ONLY AFTER MSAL is initialized. createBrowserRouter eagerly
  // runs the root loader (which reads msalInstance.getActiveAccount()) the instant
  // router.tsx evaluates — a static top-of-file import would evaluate it before the
  // init above, throwing "uninitialized_public_client_application". The dynamic
  // import defers that evaluation until init has completed.
  const { router } = await import('./router');

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <LightboxProvider>
            <RouterProvider router={router} />
          </LightboxProvider>
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
        </QueryClientProvider>
      </MsalProvider>
    </StrictMode>
  );
}
